import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { get, post, qs } from '../api/client.js';
import { useAuth } from '../api/auth.js';
import { Badge, Card, ConfirmDialog, Empty, Failure, Field, Loading, Notice } from '../components/ui.js';
import { READINESS, formatDuration } from '../lib/format.js';
import { CommandProgress, type TrackedCommand } from '../components/CommandProgress.js';

interface Device {
  id: string;
  name: string;
  serial: string;
  school: { id: string; name: string };
  readiness: string;
  online: boolean;
  status: string;
  board_profile?: string;
}

interface AudioAsset {
  id: string;
  original_name: string;
  state: string;
  duration_ms: number | null;
  is_demo: boolean;
  track_number: number | null;
}

/**
 * Manual playback (§7 page 10). The confirmation states plainly that this will
 * make a real sound in a real school, and the result never claims the sound was
 * heard — only that the device acknowledged starting output.
 */
export function PlaybackPage(): JSX.Element {
  const { can } = useAuth();
  const [schoolId, setSchoolId] = useState('');
  const [deviceId, setDeviceId] = useState('');
  // «تشغيله الآن» from the recorder opens this page with the file already chosen.
  const [searchParams] = useSearchParams();
  const [assetId, setAssetId] = useState(searchParams.get('asset') ?? '');
  const [mode, setMode] = useState<'card' | 'once'>('card');
  const [durationMs, setDurationMs] = useState(10_000);
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [tracked, setTracked] = useState<TrackedCommand | null>(null);
  const [error, setError] = useState<string | null>(null);

  const schools = useQuery({
    queryKey: ['schools', 'all'],
    queryFn: () => get<{ items: Array<{ id: string; name: string }> }>('/schools?page_size=200'),
  });

  const devices = useQuery({
    queryKey: ['devices', 'playback', schoolId],
    queryFn: () =>
      get<{ items: Device[] }>(`/devices${qs({ page_size: 200, school_id: schoolId, status: 'ACTIVE' })}`),
  });

  const assets = useQuery({
    queryKey: ['audio', 'ready'],
    queryFn: () => get<{ items: AudioAsset[] }>('/audio?page_size=200&state=READY'),
  });

  const play = useMutation({
    mutationFn: () =>
      post<{ command_id: string; expires_at: string | null; warning?: string }>(`/devices/${deviceId}/commands`, {
        type: 'play_audio',
        asset_id: assetId,
        ...(isBell ? { mode } : {}),
        max_duration_ms: durationMs,
        reason: reason || undefined,
      }),
    onSuccess: (res) => {
      setConfirming(false);
      setError(null);
      setTracked({
        deviceId,
        commandId: res.command_id,
        deviceName: device?.name ?? '',
        action:
          isBell && mode === 'once'
            ? `بث «${asset?.original_name ?? 'الملف'}» لمرة واحدة`
            : `تشغيل «${asset?.original_name ?? 'الملف'}»`,
        expiresAt: res.expires_at,
        warning: res.warning ?? null,
      });
    },
    onError: (e: Error) => {
      setConfirming(false);
      setError(e.message);
    },
  });

  const stop = useMutation({
    mutationFn: () =>
      post<{ command_id: string; expires_at: string | null }>(`/devices/${deviceId}/commands`, {
        type: 'stop_normal_audio',
      }),
    onSuccess: (res) => {
      setError(null);
      setTracked({
        deviceId,
        commandId: res.command_id,
        deviceName: device?.name ?? '',
        action: 'إيقاف الصوت اليدوي',
        expiresAt: res.expires_at,
      });
    },
    onError: (e: Error) => setError(e.message),
  });

  const device = devices.data?.items.find((d) => d.id === deviceId);
  const asset = assets.data?.items.find((a) => a.id === assetId);
  const isBell = device?.board_profile === 'esp32-dfplayer-bell';

  if (!can('playback.manual')) {
    return (
      <Card title="التشغيل اليدوي">
        <Notice tone="warn">ليست لديك صلاحية التشغيل اليدوي.</Notice>
      </Card>
    );
  }

  return (
    <>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {tracked ? <CommandProgress key={tracked.commandId} command={tracked} /> : null}

      <Card title="التشغيل اليدوي" subtitle="سيعمل الصوت فعليًا على سماعات المدرسة">
        <Notice tone="warn">
          هذه ليست معاينة. الصوت سيُشغَّل على مضخم المدرسة وسماعاتها. اختر المدرسة والجهاز والملف
          والمدة بعناية.
        </Notice>

        <div className="grid cols-2">
          <Field label="المدرسة">
            <select
              value={schoolId}
              onChange={(e) => {
                setSchoolId(e.target.value);
                setDeviceId('');
              }}
            >
              <option value="">كل المدارس</option>
              {schools.data?.items.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="الجهاز">
            {devices.isLoading ? (
              <Loading label="جارٍ تحميل الأجهزة…" />
            ) : (
              <select value={deviceId} onChange={(e) => setDeviceId(e.target.value)}>
                <option value="">اختر جهازًا</option>
                {devices.data?.items.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.school.name} — {d.name} ({d.online ? 'متصل' : 'منقطع'})
                  </option>
                ))}
              </select>
            )}
          </Field>
        </div>

        <div className="grid cols-2">
          <Field label="الملف الصوتي">
            {assets.isLoading ? (
              <Loading label="جارٍ تحميل المكتبة…" />
            ) : (assets.data?.items.length ?? 0) === 0 ? (
              <Empty title="لا توجد ملفات جاهزة" hint="ارفع ملفًا وانتظر انتهاء تحويله." />
            ) : (
              <select
                value={assetId}
                onChange={(e) => {
                  setAssetId(e.target.value);
                  const a = assets.data?.items.find((x) => x.id === e.target.value);
                  if (a?.duration_ms) setDurationMs(Math.min(300_000, a.duration_ms));
                }}
              >
                <option value="">اختر ملفًا</option>
                {assets.data!.items.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.track_number !== null && !a.original_name.startsWith(String(a.track_number).padStart(4, '0'))
                      ? `${String(a.track_number).padStart(4, '0')} · `
                      : ''}
                    {a.original_name} ({formatDuration(a.duration_ms)})
                    {a.is_demo ? ' — تجريبي' : ''}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="أقصى مدة تشغيل (ملي ثانية)" hint="الجهاز يوقف الإخراج عند انتهاء هذه المدة">
            <input
              type="number"
              min={100}
              max={300_000}
              value={durationMs}
              onChange={(e) => setDurationMs(Number(e.target.value))}
            />
          </Field>
        </div>

        {isBell ? (
          <Field label="طريقة التشغيل على جهاز الجرس">
            <div style={{ display: 'grid', gap: 6 }}>
              <label className="row" style={{ gap: 8 }}>
                <input type="radio" name="bell-mode" checked={mode === 'card'} onChange={() => setMode('card')} style={{ width: 'auto' }} />
                من بطاقة الجهاز: الملف المحفوظ عليها برقم الجرس
              </label>
              <label className="row" style={{ gap: 8 }}>
                <input type="radio" name="bell-mode" checked={mode === 'once'} onChange={() => setMode('once')} style={{ width: 'auto' }} />
                بث لمرة واحدة: ينزّله الجهاز ويشغّله ثم يحذفه دون حفظه (أجهزة النسخة 3 فقط)
              </label>
            </div>
          </Field>
        ) : null}

        <Field label="سبب التشغيل (يُسجَّل في التدقيق)">
          <input value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>

        {device && device.readiness !== 'ready' ? (
          <Notice tone="warn">
            الجهاز المختار حالته:{' '}
            <Badge tone={READINESS[device.readiness]?.tone ?? 'muted'}>
              {READINESS[device.readiness]?.label ?? device.readiness}
            </Badge>{' '}
            — قد لا ينفَّذ الأمر.
          </Notice>
        ) : null}

        <div className="row">
          <button
            className="primary"
            disabled={!deviceId || !assetId || play.isPending}
            onClick={() => setConfirming(true)}
          >
            تشغيل في المدرسة
          </button>
          <button disabled={!deviceId || stop.isPending} onClick={() => stop.mutate()}>
            إيقاف الصوت العادي
          </button>
        </div>
        <p style={{ color: 'var(--muted)', fontSize: 12.5 }}>
          «إيقاف الصوت العادي» لا يصمت أي إنذار طوارئ. إلغاء الإنذار يتم من شاشة الطوارئ بصلاحية
          مستقلة.
        </p>
      </Card>

      {confirming && device && asset ? (
        <ConfirmDialog
          title="تأكيد التشغيل الفعلي"
          confirmLabel="نعم، شغّل الآن"
          busy={play.isPending}
          message={
            <>
              سيُشغَّل <strong>{asset.original_name}</strong> على سماعات{' '}
              <strong>{device.school.name}</strong> عبر جهاز <strong>{device.name}</strong> لمدة
              أقصاها <span className="num">{formatDuration(durationMs)}</span>.
              <br />
              <br />
              هذا صوت حقيقي في مبنى مدرسي أثناء الدوام.
            </>
          }
          onCancel={() => setConfirming(false)}
          onConfirm={() => play.mutate()}
        />
      ) : null}
    </>
  );
}
